import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import FavoriteIcon from '@mui/icons-material/Favorite';

const Metronome = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioContext = useRef(null);
  const nextNoteTime = useRef(0.0);
  const timerID = useRef(null);
  const tempo = 110;
  const lookahead = 25.0; // ms
  const scheduleAheadTime = 0.1; // sec
  
  // Visual pulse state
  const [pulse, setPulse] = useState(false);

  const playClick = (time) => {
    if (!audioContext.current) return;
    
    const osc = audioContext.current.createOscillator();
    const gainNode = audioContext.current.createGain();

    osc.connect(gainNode);
    gainNode.connect(audioContext.current.destination);

    // Initial low frequency for "thud" or "beat"
    osc.frequency.setValueAtTime(150, time);
    // Drop pitch slightly for drum effect
    osc.frequency.exponentialRampToValueAtTime(0.01, time + 0.5);
    
    // Attack and decay envelope
    gainNode.gain.setValueAtTime(1, time);
    gainNode.gain.exponentialRampToValueAtTime(0.01, time + 0.5);
    
    // Play for 0.5 sec effectively
    osc.start(time);
    osc.stop(time + 0.5);
    
    // Visual feedback
    const timeToNote = (time - audioContext.current.currentTime) * 1000;
    setTimeout(() => {
        setPulse(true);
        setTimeout(() => setPulse(false), 100);
    }, Math.max(0, timeToNote));
  };

  const nextNote = () => {
    const secondsPerBeat = 60.0 / tempo;
    nextNoteTime.current += secondsPerBeat;
  };

  const scheduler = () => {
    while (nextNoteTime.current < audioContext.current.currentTime + scheduleAheadTime) {
      playClick(nextNoteTime.current);
      nextNote();
    }
    timerID.current = window.setTimeout(scheduler, lookahead);
  };

  const togglePlay = () => {
    if (isPlaying) {
      window.clearTimeout(timerID.current);
      setIsPlaying(false);
    } else {
      if (!audioContext.current) {
        audioContext.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      if (audioContext.current.state === 'suspended') {
        audioContext.current.resume();
      }

      nextNoteTime.current = audioContext.current.currentTime + 0.05;
      scheduler();
      setIsPlaying(true);
    }
  };
  
  useEffect(() => {
      return () => {
          window.clearTimeout(timerID.current);
          if (audioContext.current) {
              audioContext.current.close().catch(() => {});
              audioContext.current = null;
          }
      }
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isPlaying) {
        window.clearTimeout(timerID.current);
        setIsPlaying(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPlaying]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
        color: 'white',
        p: 2,
      }}
    >
      <Paper
        elevation={10}
        sx={{
            p: { xs: 3, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            maxWidth: '500px',
            width: '100%',
        }}
      >
        <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 1, color: '#fff', textAlign: 'center', fontSize: { xs: '2rem', md: '3rem' } }}>
            CPR Pacing
        </Typography>
        <Typography variant="h5" sx={{ mb: 4, color: '#a0aec0', letterSpacing: 2 }}>
            {tempo} BPM
        </Typography>

        <Box 
            sx={{ 
                mb: 5, 
                p: 3, 
                borderRadius: '50%', 
                background: pulse ? 'rgba(229, 62, 62, 0.8)' : 'rgba(229, 62, 62, 0.2)',
                transition: 'background 0.05s ease-in-out',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 120,
                height: 120,
                boxShadow: pulse ? '0 0 30px rgba(229, 62, 62, 0.6)' : 'none'
            }}
        >
            <FavoriteIcon sx={{ fontSize: 60, color: 'white', transform: pulse ? 'scale(1.2)' : 'scale(1)', transition: 'transform 0.05s' }} />
        </Box>

        <Button
            variant="contained"
            size="large"
            onClick={togglePlay}
            startIcon={isPlaying ? <StopIcon /> : <PlayArrowIcon />}
            sx={{
                fontSize: '1.5rem',
                py: 2,
                px: 6,
                borderRadius: 50,
                textTransform: 'none',
                fontWeight: 'bold',
                backgroundColor: isPlaying ? '#e53e3e' : '#48bb78',
                '&:hover': {
                    backgroundColor: isPlaying ? '#c53030' : '#38a169',
                    transform: 'scale(1.05)',
                },
                boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                transition: 'all 0.2s ease-in-out',
                minWidth: '200px'
            }}
        >
            {isPlaying ? 'STOP' : 'START'}
        </Button>
        
        <Typography variant="caption" sx={{ mt: 4, color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
            Audible click at 110 beats per minute.<br/>
            Stay calm. Compress to the beat.
        </Typography>
      </Paper>
    </Box>
  );
};

export default Metronome;
